import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  Checkbox,
  Search,
  SelectInput,
  SelectInputItem,
  Textarea,
} from '@ads/components-react';
import { BoltIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 20;

import type { EnrollmentScholarshipStatus, EnrollmentStatus } from '../../../types/enrollment';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';
import { ClassAutocomplete } from '../../molecules/ClassAutocomplete';

// ─── Local Types ─────────────────────────────────────────────────────────────

export interface BatchEligibleStudent {
  readonly id: number;
  readonly initials: string;
  readonly name: string;
  readonly enrollmentDate: string;
  readonly attendance: number | null;
  readonly gradeAvg: number | null;
  readonly gradeExam: number | null;
  readonly scholarshipStatus: EnrollmentScholarshipStatus;
  readonly hasMonograph: boolean;
  readonly currentStatus: EnrollmentStatus;
}

export interface EnrollmentBatchStatusTemplateProps {
  /** ID da turma selecionada (controlado); `''` quando nenhuma turma está selecionada. */
  readonly classId: string;
  readonly onClassChange: (classId: string) => void;
  /** Status destino selecionado (controlado); `null` quando nenhum status está selecionado. */
  readonly newStatus: EnrollmentStatus | null;
  readonly onNewStatusChange: (status: EnrollmentStatus | null) => void;
  readonly eligibleStudents: readonly BatchEligibleStudent[];
  /** Indica que a lista de alunos elegíveis está sendo carregada. */
  readonly isLoading?: boolean;
  /** Indica que a mudança em lote está sendo aplicada (mutation em andamento). */
  readonly isApplying?: boolean;
  readonly totalEligibleCount: number;
  readonly directCount: number;
  readonly revertCount: number;
  readonly hasCertificate: boolean;
  readonly onApply: (data: BatchFormValues) => void;
  readonly onSaveDraft: (data: BatchFormValues) => void;
}

export interface BatchFormValues {
  readonly classId: string;
  readonly newStatus: EnrollmentStatus | null;
  readonly scholarshipFilter: string;
  readonly sendNotification: boolean;
  readonly sendCertificate: boolean;
  readonly observation: string;
  readonly selectedIds: readonly number[];
}

/**
 * Valor-sentinela para o item de placeholder dos `SelectInput`. O Radix Select
 * reserva a string vazia para "nada selecionado", então um item não pode usar
 * `value=""`. Externamente, "nada selecionado" continua sendo `''` (turma) ou
 * `null` (status) — o sentinela fica encapsulado no template.
 */
const NONE_VALUE = '__none__';

// ─── Status options (targetable statuses for batch) ──────────────────────────

const BATCH_TARGET_STATUSES: EnrollmentStatus[] = [
  'approved',
  'active',
  'finished',
  'finished_with_certificate',
  'finished_with_monograph',
  'canceled',
  'canceled_in_registration',
  'rejected',
  'blocked',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  return `${value}%`;
}

function formatGrade(value: number | null): string {
  if (value === null) return '—';
  return value.toFixed(1).replace('.', ',');
}

// ─── SectionCard ─────────────────────────────────────────────────────────────

function SectionCard({
  title,
  headerRight,
  children,
}: {
  title: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
      <div className="flex items-center justify-between gap-xs border-b-hairline border-ctx-layout-border px-xxs py-nano">
        <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
        {headerRight && <div className="flex items-center gap-xxs">{headerRight}</div>}
      </div>
      <div className="px-xxs py-xxxs">{children}</div>
    </div>
  );
}

// ─── Section 2 note ──────────────────────────────────────────────────────────

function Section2Note({ statusLabel }: { statusLabel: string }) {
  const { t } = useTranslation('personManagement');
  return (
    <p className="ts-paragraph-xs text-ctx-content-placeholder">
      {statusLabel
        ? `${t('batchStatus.section2.noteOptional', { status: statusLabel })} ${t('batchStatus.section2.noteRequiredPre')} `
        : `${t('batchStatus.section2.noteRequiredPre')} `}
      <span className="!font-medium">{t('batchStatus.section2.noteRequiredCanceled')}</span>
      {', '}
      <span className="!font-medium">{t('batchStatus.section2.noteRequiredCanceledInReg')}</span>
      {' ou '}
      <span className="!font-medium">{t('batchStatus.section2.noteRequiredRejected')}</span>
      {t('batchStatus.section2.noteRequiredSuffix')}
    </p>
  );
}

// ─── Table header ─────────────────────────────────────────────────────────────

const TABLE_GRID =
  'grid grid-cols-[2rem_minmax(0,1fr)_6.5rem_4.5rem_4.5rem_5rem_5.5rem_3.5rem_9rem]';

function StudentTableHeader({
  allSelected,
  onSelectAll,
}: {
  allSelected: boolean;
  onSelectAll: (v: boolean) => void;
}) {
  const { t } = useTranslation('personManagement');
  return (
    <div
      className={`${TABLE_GRID} sticky top-none z-10 border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-xxs`}
    >
      <div className="flex items-center">
        <Checkbox checked={allSelected} onCheckedChange={v => onSelectAll(v === true)} size="sm" />
      </div>
      {(
        [
          'batchStatus.table.student',
          'batchStatus.table.date',
          'batchStatus.table.freq',
          'batchStatus.table.gradeAvg',
          'batchStatus.table.gradeExam',
          'batchStatus.table.scholarship',
          'batchStatus.table.mono',
          'batchStatus.table.currentStatus',
        ] as const
      ).map(key => (
        <span
          key={key}
          className="ts-caption-xxs !font-medium uppercase tracking-wide text-ctx-content-placeholder"
        >
          {t(key)}
        </span>
      ))}
    </div>
  );
}

// ─── Student row ─────────────────────────────────────────────────────────────

function StudentRow({
  student,
  selected,
  onToggle,
}: {
  student: BatchEligibleStudent;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('personManagement');
  return (
    <div
      className={`${TABLE_GRID} items-center border-b-hairline border-ctx-layout-border px-xs py-xxs transition-colors last:border-b-none hover:bg-ctx-interactive-secondary ${selected ? 'bg-ctx-interactive-secondary' : 'bg-ctx-layout-surface'}`}
    >
      <div className="flex items-center">
        <Checkbox checked={selected} onCheckedChange={onToggle} size="sm" />
      </div>

      {/* Avatar + name */}
      <div className="flex min-w-none items-center gap-xs">
        <span className="flex h-xs w-xs shrink-0 items-center justify-center rounded-circular bg-ctx-interactive-secondary ts-caption-xxs !font-medium text-ctx-content-brand">
          {student.initials}
        </span>
        <span className="truncate ts-paragraph-xs text-ctx-content-title">{student.name}</span>
      </div>

      <span className="ts-paragraph-xs text-ctx-content-base">{student.enrollmentDate}</span>
      <span className="ts-paragraph-xs text-ctx-content-base">
        {formatPercent(student.attendance)}
      </span>
      <span className="ts-paragraph-xs text-ctx-content-base">{formatGrade(student.gradeAvg)}</span>
      <span className="ts-paragraph-xs text-ctx-content-base">
        {formatGrade(student.gradeExam)}
      </span>
      <span className="ts-paragraph-xs text-ctx-content-base">
        {student.scholarshipStatus === 'none'
          ? '—'
          : student.scholarshipStatus === 'partial'
            ? t('scholarshipStatus.partial')
            : t('scholarshipStatus.full')}
      </span>
      <span className="ts-paragraph-xs text-ctx-content-base">
        {student.hasMonograph ? t('batchStatus.table.monoYes') : '—'}
      </span>
      <div className="flex items-center">
        <EnrollmentStatusBadge status={student.currentStatus} />
      </div>
    </div>
  );
}

// ─── Main template ────────────────────────────────────────────────────────────

/**
 * Template for the batch enrollment status change page.
 * Renders 3 sequential sections: class/status selection, transition
 * configuration, and eligible students list with checkboxes.
 */
export function EnrollmentBatchStatusTemplate({
  classId,
  onClassChange,
  newStatus,
  onNewStatusChange,
  eligibleStudents,
  isLoading = false,
  isApplying = false,
  totalEligibleCount,
  directCount,
  revertCount,
  hasCertificate,
  onApply,
  onSaveDraft,
}: EnrollmentBatchStatusTemplateProps) {
  const { t } = useTranslation('personManagement');
  const { t: tCommon } = useTranslation('common');

  const [scholarshipFilter, setScholarshipFilter] = useState('all');
  const [sendNotification, setSendNotification] = useState(false);
  const [sendCertificate, setSendCertificate] = useState(false);
  const [observation, setObservation] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EnrollmentStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const resolvedStatus = newStatus;
  const resolvedClassId = classId;

  const statusLabel = resolvedStatus !== null ? t(`enrollmentStatus.${resolvedStatus}`) : '';

  const visibleStudents = eligibleStudents.filter(s => {
    const matchesSearch =
      searchQuery === '' || s.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.currentStatus === statusFilter;
    const matchesScholarship =
      scholarshipFilter === 'all' || s.scholarshipStatus === scholarshipFilter;
    return matchesSearch && matchesStatus && matchesScholarship;
  });

  const totalPages = Math.max(1, Math.ceil(visibleStudents.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedStudents = visibleStudents.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (filter: EnrollmentStatus | 'all') => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  const allPageSelected =
    pagedStudents.length > 0 && pagedStudents.every(s => selectedIds.has(s.id));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        for (const s of pagedStudents) next.add(s.id);
      } else {
        for (const s of pagedStudents) next.delete(s.id);
      }
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildValues = (): BatchFormValues => ({
    classId: resolvedClassId,
    newStatus: resolvedStatus,
    scholarshipFilter,
    sendNotification,
    sendCertificate,
    observation,
    selectedIds: [...selectedIds],
  });

  return (
    <div className="flex flex-col gap-xs">
      <Breadcrumb>
        <BreadcrumbLink>
          <Link to="/">
            <BreadcrumbIcon />
          </Link>
        </BreadcrumbLink>
        <BreadcrumbLink>
          <Link to="/enrollments">{t('breadcrumb.enrollments')}</Link>
        </BreadcrumbLink>
        <BreadcrumbPage>{t('breadcrumb.batchStatus')}</BreadcrumbPage>
      </Breadcrumb>

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-quark">
        <h1 className="ts-heading-md text-ctx-content-title">{t('batchStatus.title')}</h1>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">{t('batchStatus.subtitle')}</p>
      </div>

      {/* ── Section 1 ── Turma e status ─────────────────────────────────────── */}
      <SectionCard title={`1. ${t('batchStatus.section1.title')}`}>
        <div className="flex flex-col gap-xs">
          <div className="grid grid-cols-1 gap-xs md:grid-cols-3">
            {/* Turma */}
            <ClassAutocomplete
              value={classId}
              onChange={onClassChange}
              label={t('batchStatus.section1.classLabel')}
              placeholder={t('batchStatus.section1.classPlaceholder')}
            />

            {/* Novo status */}
            <SelectInput
              label={t('batchStatus.section1.newStatusLabel')}
              size="md"
              fullWidth
              value={newStatus ?? NONE_VALUE}
              onValueChange={v =>
                onNewStatusChange(v === NONE_VALUE ? null : (v as EnrollmentStatus))
              }
            >
              <SelectInputItem value={NONE_VALUE} custom={{ root: 'text-nowrap' }}>
                {t('batchStatus.section1.newStatusPlaceholder')}
              </SelectInputItem>
              {BATCH_TARGET_STATUSES.map(s => (
                <SelectInputItem key={s} value={s} custom={{ root: 'text-nowrap' }}>
                  {t(`enrollmentStatus.${s}`)}
                </SelectInputItem>
              ))}
            </SelectInput>

            {/* Filtro de bolsa */}
            <SelectInput
              label={t('batchStatus.section1.scholarshipFilterLabel')}
              size="md"
              fullWidth
              value={scholarshipFilter}
              onValueChange={setScholarshipFilter}
            >
              <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
                {t('batchStatus.section1.scholarshipAll')}
              </SelectInputItem>
              <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                {t('batchStatus.section1.scholarshipNone')}
              </SelectInputItem>
              <SelectInputItem value="partial" custom={{ root: 'text-nowrap' }}>
                {t('batchStatus.section1.scholarshipPartial')}
              </SelectInputItem>
              <SelectInputItem value="full" custom={{ root: 'text-nowrap' }}>
                {t('batchStatus.section1.scholarshipFull')}
              </SelectInputItem>
            </SelectInput>
          </div>

          {/* Eligibility summary */}
          {resolvedClassId && resolvedStatus && (
            <div className="flex items-start gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-mini">
              <InformationCircleIcon className="mt-quark h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />
              <div className="flex flex-col gap-quark">
                <div className="flex flex-wrap items-center gap-mini">
                  <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                    {t('batchStatus.section1.eligibleCount', {
                      count: totalEligibleCount,
                    })}
                  </span>
                  <span className="ts-paragraph-xs text-ctx-content-base">·</span>
                  <span className="ts-paragraph-xs text-ctx-content-base">
                    {t('batchStatus.section1.eligibleBreakdown', {
                      direct: directCount,
                      status: statusLabel,
                      revert: revertCount,
                    })}
                  </span>
                </div>
                <span className="ts-caption-xxs !font-medium text-ctx-content-placeholder">
                  {t('batchStatus.section1.forceNote')}
                </span>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ── Section 2 ── Configuração da transição ────────────────────────── */}
      <SectionCard title={`2. ${t('batchStatus.section2.title')}`}>
        <div className="flex flex-col gap-xs">
          <Section2Note statusLabel={statusLabel} />

          {/* Checkboxes — 2-column grid */}
          <div className="grid grid-cols-1 gap-xxs md:grid-cols-2">
            <Checkbox
              checked={sendNotification}
              onCheckedChange={v => setSendNotification(v === true)}
              label={t('batchStatus.section2.sendNotification')}
              size="sm"
            />
            {hasCertificate && (
              <Checkbox
                checked={sendCertificate}
                onCheckedChange={v => setSendCertificate(v === true)}
                label={t('batchStatus.section2.sendCertificate')}
                size="sm"
              />
            )}
          </div>

          {/* Observation */}
          <Textarea
            label={t('batchStatus.section2.observation')}
            maxLength={500}
            resizable
            fullWidth
            value={observation}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setObservation(e.target.value)}
          />
        </div>
      </SectionCard>

      {/* ── Section 3 ── Alunos elegíveis ───────────────────────────────────── */}
      <SectionCard
        title={`3. ${t('batchStatus.section3.title', { count: visibleStudents.length })}`}
        headerRight={
          <>
            <Search
              value={searchQuery}
              onChange={e => handleSearchChange(e.target.value)}
              label={t('batchStatus.section3.searchPlaceholder')}
              placeholder={t('batchStatus.section3.searchPlaceholder')}
              size="md"
              custom={{ inputWrapper: '!w-[24rem]' }}
            />
            <SelectInput
              label={t('batchStatus.section3.filterStatusLabel')}
              size="md"
              value={statusFilter}
              onValueChange={v => handleStatusFilterChange(v as EnrollmentStatus | 'all')}
              custom={{ triggerWrapper: '!w-[15rem]' }}
            >
              <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
                {t('batchStatus.section3.filterAll')}
              </SelectInputItem>
              {BATCH_TARGET_STATUSES.map(s => (
                <SelectInputItem key={s} value={s} custom={{ root: 'text-nowrap' }}>
                  {t(`enrollmentStatus.${s}`)}
                </SelectInputItem>
              ))}
            </SelectInput>
          </>
        }
      >
        {isLoading ? (
          <p className="py-xs text-center ts-paragraph-xs text-ctx-content-placeholder">
            {t('batchStatus.section3.loading')}
          </p>
        ) : visibleStudents.length === 0 ? (
          <p className="py-xs text-center ts-paragraph-xs text-ctx-content-placeholder">
            {eligibleStudents.length === 0
              ? t('batchStatus.section3.empty')
              : t('batchStatus.section3.emptyFilter')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <div className="min-w-[58rem]">
                <StudentTableHeader allSelected={allPageSelected} onSelectAll={toggleAll} />
                {pagedStudents.map(student => (
                  <StudentRow
                    key={student.id}
                    student={student}
                    selected={selectedIds.has(student.id)}
                    onToggle={() => toggleOne(student.id)}
                  />
                ))}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex flex-col gap-xs border-t-hairline border-ctx-layout-border px-mini py-xxs sm:flex-row sm:items-center sm:justify-between">
                <p className="ts-caption-xxs text-ctx-content-placeholder">
                  {tCommon('table.pagination.showing', {
                    from: (safePage - 1) * PAGE_SIZE + 1,
                    to: Math.min(safePage * PAGE_SIZE, visibleStudents.length),
                    total: visibleStudents.length,
                  })}
                </p>
                <div className="flex items-center gap-nano">
                  <Button
                    hierarchy="secondary"
                    size="sm"
                    onClick={() => setCurrentPage(safePage - 1)}
                    disabled={safePage <= 1}
                  >
                    ← {tCommon('table.pagination.previous')}
                  </Button>
                  <span className="ts-caption-xxs text-ctx-content-base">
                    {safePage} / {totalPages}
                  </span>
                  <Button
                    hierarchy="secondary"
                    size="sm"
                    onClick={() => setCurrentPage(safePage + 1)}
                    disabled={safePage >= totalPages}
                  >
                    {tCommon('table.pagination.next')} →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* ── Sticky footer ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-xxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-xxxs">
        {/* Left — selection controls */}
        <div className="flex flex-wrap items-center gap-xxs">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
            {t('batchStatus.footer.selectedCount', {
              selected: selectedIds.size,
              total: totalEligibleCount,
            })}
          </span>
          <Button hierarchy="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
            {t('batchStatus.footer.deselectAll')}
          </Button>
          <Button
            hierarchy="secondary"
            size="sm"
            onClick={() => setSelectedIds(new Set(eligibleStudents.map(s => s.id)))}
          >
            {t('batchStatus.footer.selectAll', {
              count: totalEligibleCount,
            })}
          </Button>
        </div>

        {/* Right — summary + actions */}
        <div className="flex flex-wrap items-center gap-xxs">
          {selectedIds.size > 0 && resolvedStatus !== null && (
            <div className="flex items-center gap-mini rounded-sm w-full border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-quark">
              <InformationCircleIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />
              <p className="ts-paragraph-xs text-ctx-content-base">
                <b className="!font-medium text-ctx-content-title">
                  {t('batchStatus.footer.summaryCount', {
                    count: selectedIds.size,
                  })}
                </b>{' '}
                {t('batchStatus.footer.summaryMoveTo')}{' '}
                <b className="!font-medium text-ctx-content-title">{statusLabel}</b>
                {t('batchStatus.footer.summarySuffix')}
              </p>
            </div>
          )}
          <Button
            hierarchy="secondary"
            size="sm"
            disabled={isApplying}
            onClick={() => onSaveDraft(buildValues())}
          >
            {t('batchStatus.footer.saveDraft')}
          </Button>
          <Button
            hierarchy="primary"
            size="sm"
            leadingIcon={BoltIcon}
            isLoading={isApplying}
            disabled={selectedIds.size === 0 || resolvedStatus === null || isApplying}
            onClick={() => onApply(buildValues())}
          >
            {t('batchStatus.footer.apply')}
          </Button>
        </div>
      </div>
    </div>
  );
}
